// Mahjong pointer input: normalizes canvas pointer + wheel events into the
// canvas/CSS-pixel coordinate space the layout + hit tests work in. Forwards
// every pointer (not just the primary) and the full down/move/up/cancel
// lifecycle — the stateful pan/pinch/tap decisions live in
// `ViewportController`, so this stays a dumb normalizer, mirroring domino's
// InputHandler/PointerDispatcher split.

import { pointerKind, type PointerKind } from '@bn-games/input-core';

export type InteractionKind = 'down' | 'move' | 'up' | 'leave' | 'cancel';

export interface PointerInteraction {
  readonly kind: InteractionKind;
  readonly canvasX: number;
  readonly canvasY: number;
  readonly pointer: PointerKind;
  readonly pointerId: number;
}

export interface WheelInteraction {
  readonly canvasX: number;
  readonly canvasY: number;
  /** Sign of deltaY: +1 (zoom out) or -1 (zoom in). */
  readonly deltaSign: 1 | -1;
}

export interface InputHandlerOptions {
  readonly canvas: HTMLCanvasElement;
  /** Viewport (CSS-pixel) size — re-read per event so resizes need no re-subscribe. */
  readonly getViewport: () => { readonly width: number; readonly height: number };
  readonly onInteraction: (event: PointerInteraction) => void;
  readonly onWheel: (event: WheelInteraction) => void;
}

export class InputHandler {
  private readonly disposers: Array<() => void> = [];

  constructor(private readonly opts: InputHandlerOptions) {
    this.attach();
  }

  private attach(): void {
    const { canvas } = this.opts;

    // Shared by pointer and wheel events — both are MouseEvent subtypes with
    // clientX/clientY, but @bn-games/input-core's helper is typed to
    // PointerEvent specifically, so this inlines the same rect-relative math.
    const coords = (clientX: number, clientY: number): { x: number; y: number } => {
      const { width, height } = this.opts.getViewport();
      const rect = canvas.getBoundingClientRect();
      return { x: ((clientX - rect.left) / rect.width) * width, y: ((clientY - rect.top) / rect.height) * height };
    };

    const dispatch = (kind: InteractionKind, e: PointerEvent): void => {
      const { x, y } = coords(e.clientX, e.clientY);
      this.opts.onInteraction({
        kind,
        canvasX: x,
        canvasY: y,
        pointer: pointerKind(e),
        pointerId: e.pointerId,
      });
    };

    const onDown = (e: PointerEvent): void => {
      dispatch('down', e);
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Some platforms throw if already captured; safe to ignore.
      }
    };
    const onMove = (e: PointerEvent): void => dispatch('move', e);
    const onUp = (e: PointerEvent): void => {
      dispatch('up', e);
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Already released — ignore.
      }
    };
    const onCancel = (e: PointerEvent): void => dispatch('cancel', e);
    const onLeave = (e: PointerEvent): void => dispatch('leave', e);
    const onDragStart = (e: DragEvent): void => e.preventDefault();
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const { x, y } = coords(e.clientX, e.clientY);
      this.opts.onWheel({ canvasX: x, canvasY: y, deltaSign: e.deltaY > 0 ? 1 : -1 });
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('dragstart', onDragStart);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    this.disposers.push(
      () => canvas.removeEventListener('pointerdown', onDown),
      () => canvas.removeEventListener('pointermove', onMove),
      () => canvas.removeEventListener('pointerup', onUp),
      () => canvas.removeEventListener('pointercancel', onCancel),
      () => canvas.removeEventListener('pointerleave', onLeave),
      () => canvas.removeEventListener('dragstart', onDragStart),
      () => canvas.removeEventListener('wheel', onWheel),
    );
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers.length = 0;
  }
}
